import React, { useRef, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { NavLink } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import './MenuItemWithSubmenu.css';

/**
 * 🎯 Componente para item de menu com submenu
 * - Suporta abertura/fechamento do submenu
 * - Estados ativos para menu e submenus
 * - Animações suaves de transição
 * - Filtra submenus baseado em permissões
 */
const MenuItemWithSubmenu = ({ 
  menuKey, 
  menuData, 
  isOpen, 
  isActive, 
  onToggle, 
  isRouteActive,
  hasPermission = true 
}) => {
  const { canAccessRoute, isOwner } = usePermissions();
  const menuItemRef = useRef(null);
  const submenuRef = useRef(null);
  const [submenuTop, setSubmenuTop] = useState(0);

  // Filtrar submenus baseado em permissões
  const visibleSubmenus = menuData.submenus.filter(submenu => {
    // Owner pode ver tudo
    if (isOwner()) return true;
    
    // Para outros usuários, verificar se podem acessar a rota
    const canAccess = canAccessRoute(submenu.path);
    
    return canAccess;
  });

  // Calcular posição do submenu e tooltip quando collapsed
  useEffect(() => {
    const updatePositions = () => {
      if (menuItemRef.current && submenuRef.current) {
        const isCollapsed = document.querySelector('.sidebar-menu-collapsed');
        if (isCollapsed) {
          const rect = menuItemRef.current.getBoundingClientRect();
          setSubmenuTop(rect.top);
          submenuRef.current.style.setProperty('--submenu-top', `${rect.top}px`);
          
          // Calcular posição do tooltip
          const menuHeader = menuItemRef.current.querySelector('.menu-header');
          if (menuHeader) {
            menuHeader.style.setProperty('--tooltip-top', `${rect.top}px`);
          }
        }
      }
    };

    const handleMouseEnter = () => {
      updatePositions();
    };

    const menuItem = menuItemRef.current;
    if (menuItem) {
      menuItem.addEventListener('mouseenter', handleMouseEnter);
      return () => {
        menuItem.removeEventListener('mouseenter', handleMouseEnter);
      };
    }
  }, []);

  // Verificar permissão DEPOIS dos hooks
  if (!hasPermission) return null;

  // Se não há submenus visíveis, não mostrar o menu
  if (visibleSubmenus.length === 0) return null;

  return (
    <li 
      ref={menuItemRef}
      className={`menu-item-with-submenu ${isActive ? 'menu-active' : ''} ${isOpen ? 'menu-open' : ''}`}
    >
      {/* Menu Principal */}
      <div 
        className={`menu-header ${isActive ? 'active-menu' : ''}`}
        onClick={() => onToggle(menuKey)}
        style={{ 
          cursor: 'pointer',
          ...(submenuTop > 0 ? { '--tooltip-top': `${submenuTop}px` } : {})
        }}
        title={menuData.label}
      >
        <Icon icon={menuData.icon} className="menu-icon" />
        <span className="menu-label">{menuData.label}</span>
        <Icon 
          icon="mdi:chevron-right" 
          className={`menu-toggle-icon ${isOpen ? 'rotated' : ''}`} 
        />
      </div>

      {/* Submenu */}
      <div 
        ref={submenuRef}
        className={`submenu-container ${isOpen ? 'submenu-open' : 'submenu-closed'}`}
        style={submenuTop > 0 ? { 
          '--submenu-top': `${submenuTop}px`,
          top: `${submenuTop}px`
        } : {}}
      >
        <ul className="submenu-list">
          {visibleSubmenus.map((submenu, index) => (
            <li key={index} className="submenu-item">
              {submenu.submenus ? (
                // Submenu com sub-submenus (como AfterPay)
                <div className="submenu-with-nested">
                  <div className="submenu-header">
                    <Icon icon={submenu.icon} className="submenu-icon" />
                    <span className="submenu-label">{submenu.label}</span>
                    <Icon icon="mdi:chevron-right" className="submenu-chevron" />
                  </div>
                  <ul className="nested-submenu-list">
                    {submenu.submenus.map((nestedSubmenu, nestedIndex) => (
                      <li key={nestedIndex} className="nested-submenu-item">
                        <NavLink
                          to={nestedSubmenu.path}
                          className={({ isActive }) => 
                            `nested-submenu-link ${isActive ? 'nested-submenu-active' : ''}`
                          }
                        >
                          <Icon icon={nestedSubmenu.icon} className="nested-submenu-icon" />
                          <span className="nested-submenu-label">{nestedSubmenu.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                // Submenu normal
                <NavLink
                  to={submenu.path}
                  className={({ isActive }) => 
                    `submenu-link ${isActive ? 'submenu-active' : ''}`
                  }
                >
                  <Icon icon={submenu.icon} className="submenu-icon" />
                  <span className="submenu-label">{submenu.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </div>

    </li>
  );
};

export default MenuItemWithSubmenu;
